<template>
  <div id="attachments" tabindex="-1">
    <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
    <base-field-with-edit-btn
      :default-to-edit-mode="isViewingAsCompany && !files.length > 0"
      :prevent-edits="!canEdit || !isViewingAsCompany"
      :field-label="title"
      :field-help-text="helperText"
    >
      <template #content-for-display-mode>
        <div class="m-t-10">
          <ContractFormFilesList
            :contract-id="contractId"
            :files="selectedFiles"
            :is-viewing-as-company="isViewingAsCompany"
            :needs-review="!isViewingAsCompany && containsUnreviewedFiles"
            :show-changes="showChanges"
          />
        </div>
      </template>
      <template #content-for-edit-mode>
        <small class="c-gray m-b-10">
          {{ helperText }}
        </small>
        <br />
        <ContractFormFilesList
          :contract-id="contractId"
          :edit="true"
          :files="selectedFiles"
          :is-viewing-as-company="isViewingAsCompany"
          :show-changes="showChanges"
          @toggle-file-deletion="toggleFileDeletion"
        />
        <div class="m-t-10">
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <PickerFileFilebank
            v-slot="{ openFilePicker }"
            :no-top-margin="true"
            :default-tags="[$t('Contracts')]"
            :exclude-ids="selectedFiles.map((f) => f.localId)"
            @files-selected="addFiles"
          >
            <span
              v-if="!showFileUpload"
              class="btn btn-primary btn-xs pointer"
              @click="handleClick(openFilePicker)"
            >
              + {{ $t('Add files') }}
            </span>
          </PickerFileFilebank>
        </div>
        <div class="small c-gray m-t-10">
          {{
            totalFileCount === 1
              ? $t('1 file selected')
              : $t('(:count) files selected', { count: totalFileCount })
          }}
        </div>
        <input
          v-for="id in nonRemovedFileSelectionIds"
          :key="id"
          :value="id"
          type="hidden"
          name="attachments[]"
        />
      </template>
    </base-field-with-edit-btn>
  </div>
</template>

<script>
import ContractFormFilesList from '@/components/ContractFormFilesList.vue'

export default {
  components: { ContractFormFilesList },
  props: {
    files: { type: Array, default: () => [] },
    filesChanged: { type: Array, default: () => [] },
    perspective: { type: String, default: 'company' },
    containsUnreviewedFiles: { type: Boolean, default: false },
    // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
    canEdit: { type: Boolean, default: true },
    contractId: { type: Number, required: true },
    showChanges: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedFiles: this.initialSelectedFiles(),
      showFileUpload: false,
    }
  },
  computed: {
    filePropIds() {
      return this.files.map((f) => f.id || f.localId)
    },
    nonRemovedFileSelectionIds() {
      return this.nonRemovedFileSelection.map((f) => f.localId)
    },
    totalFileCount() {
      return this.nonRemovedFileSelection.length
    },
    nonRemovedFileSelection() {
      return this.selectedFiles.filter((f) => f.isRemoved !== true)
    },
    isViewingAsCompany() {
      return this.perspective === 'company'
    },
    selectedFileIds() {
      return this.selectedFiles.map((f) => f.localId)
    },
    helperText() {
      if (this.isViewingAsCompany) {
        return this.containsUnreviewedFiles || this.canEdit
          ? $t(
              'The worker will need to review these documents as part of the contract acceptance process',
            )
          : $t('The freelancer has reviewed and accepted the files prior to accepting the contract')
      }
      return this.containsUnreviewedFiles
        ? $t('These files must be reviewed and accepted prior to accepting the contract')
        : $t('You have reviewed and accepted these files prior to accepting the contract')
    },
    allFilesReviewed() {
      return !this.selectedFiles.some((f) => f.isReviewed === false)
    },
    title() {
      let title = $t('Supplemental Documents')
      if (this.containsUnreviewedFiles) {
        title += ` (${this.isViewingAsCompany ? $t('awaiting review') : $t('updated')})`
      }
      return title
    },
    containsChanges() {
      if (this.filePropIds.length !== this.nonRemovedFileSelectionIds.length) {
        return true
      }
      // Find difference between two arrays
      return (
        this.filePropIds
          .filter((x) => !this.nonRemovedFileSelectionIds.includes(x))
          .concat(this.nonRemovedFileSelectionIds.filter((x) => !this.filePropIds.includes(x)))
          .length > 0
      )
    },
  },
  watch: {
    allFilesReviewed(newValue) {
      this.emitFileReviewEvents()
    },
    containsChanges(newValue) {
      this.$emit('toggleHasFileChanges', newValue)
    },
  },
  mounted() {
    this.emitFileReviewEvents()
  },
  methods: {
    scrollToTop() {
      $('.modal').scrollTop(0)
    },
    addFiles(fileArray) {
      const incomingFiles = fileArray
        .filter((f) => !this.selectedFileIds.includes(f.localId))
        .map((f) => ({
          ...f,
          localId: f.id || f.localId,
          isReviewed: false,
          isRemoved: false,
          storage_name: f.storageName,
          isRecentlyAdded: true,
        }))
      this.selectedFiles.push(...incomingFiles)
    },
    initialSelectedFiles() {
      return this.files.length > 0
        ? this.files.map((f) => ({
            ...f,
            localId: f.id || f.localId,
            isReviewed: false,
            isRemoved: !!this.filesChanged.find((fC) => f.id === fC.id),
            isRecentlyAdded: !this.filesChanged.find((fC) => f.id === fC.id),
          }))
        : []
    },
    toggleFileDeletion(k) {
      this.selectedFiles[k].isRemoved = !this.selectedFiles[k].isRemoved
    },
    emitFileReviewEvents() {
      if (this.perspective !== 'company') {
        if (this.allFilesReviewed) {
          this.$emit('allFilesReviewed')
        } else {
          this.$emit('notAllFilesReviewed')
        }
      }
    },
    handleClick(openFilePicker) {
      openFilePicker()
      this.scrollToTop()
    },
  },
}
</script>
